/**
 * / — root route. Pure traffic-directing component.
 *
 * The SPA has no public "/" page (the marketing site lives on Next.js
 * at www.workright.in). For an authenticated session we send the user
 * to /dashboard; for everyone else we send them to /login. We READ the
 * AuthProvider context here so the redirect is resolved in a single
 * render once the mount-time /me probe settles — no flicker, no extra
 * fetch.
 *
 * Why not `beforeLoad` + `throw redirect()`? AuthProvider runs INSIDE
 * the RouterProvider, so `beforeLoad` (which fires before any
 * component renders) can't read the React context yet. Using a
 * component-level `<Navigate />` keeps the auth model centralised in
 * the provider — the route just observes it.
 */

import { Navigate, createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/")({
  component: IndexRoute,
});

function IndexRoute() {
  const { loading, user, identityOnly } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }

  // identity-only = authenticated but no active membership → picker.
  if (user && identityOnly) {
    return <Navigate to="/select-workspace" replace />;
  }

  if (user) {
    return (
      <Navigate
        to="/app/$tenantSlug"
        params={{ tenantSlug: user.tenant?.slug ?? "" }}
        replace
      />
    );
  }

  return <Navigate to="/login" replace />;
}
